import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Shield } from "lucide-react";
import PasswordInput from "@/components/PasswordInput";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);

  useEffect(() => {
    if (loc.state?.error) setError(loc.state.error);
  }, [loc.state]);

  const sendReset = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) return;
    setForgotBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail.trim() });
      toast.success("If an account exists for that email, a reset link is on its way.");
      setForgotOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setForgotBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await login(email.trim(), password);
      toast.success(`Welcome, ${u.first_name}`);
      nav(u.role === "admin" ? "/admin" : "/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-950 text-slate-100">
      <div className="hidden lg:flex flex-col justify-between p-10 border-r border-slate-800 grid-overlay">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 grid place-items-center rounded-md bg-blue-600/20 border border-blue-500/40">
            <Shield className="h-6 w-6 text-blue-400" />
          </div>
          <div>
            <div className="font-heading text-2xl font-semibold tracking-tight">Crew Ready</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Risk · Rest · Readiness</div>
          </div>
        </div>
        <div>
          <h1 className="font-heading text-5xl font-bold tracking-tight leading-[1.05]">
            Command<br />the fatigue<br /><span className="text-blue-400">before</span> it commands you.
          </h1>
          <p className="mt-6 max-w-md text-slate-400 leading-relaxed">
            A tactical readiness console for safety-critical crews. Log rest, score risk in real time,
            and give supervisors clear RAG signals before the duty period begins.
          </p>
        </div>
        <div className="flex gap-3 text-[10px] uppercase tracking-widest font-mono-num text-slate-500">
          <span className="rag-green px-2 py-1 rounded">GREEN · Fit</span>
          <span className="rag-yellow px-2 py-1 rounded">YELLOW · Caution</span>
          <span className="rag-amber px-2 py-1 rounded">AMBER · Elevated</span>
          <span className="rag-red px-2 py-1 rounded">RED · Critical</span>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-10">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
          <div className="mb-8">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-2">Secure Sign-In</div>
            <h2 className="font-heading text-3xl font-semibold tracking-tight">Sign in to your console</h2>
            <p className="text-sm text-slate-400 mt-2">Enter your credentials to continue.</p>
          </div>
          <label className="block text-xs uppercase tracking-widest text-slate-400 font-mono-num mb-1.5">Email</label>
          <input
            data-testid="login-email"
            type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3.5 py-2.5 rounded-md bg-slate-900 border border-slate-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/40 outline-none text-sm"
            placeholder="you@company.com"
          />
          <label className="block text-xs uppercase tracking-widest text-slate-400 font-mono-num mt-4 mb-1.5">Password</label>
          <PasswordInput
            testId="login-password"
            required value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3.5 py-2.5 rounded-md bg-slate-900 border border-slate-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/40 outline-none text-sm"
          />
          <div className="mt-2 text-right">
            <button type="button" data-testid="forgot-password-link"
              onClick={() => { setForgotEmail(email); setForgotOpen(true); }}
              className="text-xs text-blue-400 hover:text-blue-300 transition-colors">
              Forgot password?
            </button>
          </div>
          {error && <div data-testid="login-error" className="mt-4 text-sm rag-red px-3 py-2 rounded">{error}</div>}
          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="mt-6 w-full py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white font-medium transition-colors disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>
          <div className="mt-4 flex items-center gap-3 text-[10px] uppercase tracking-widest text-slate-600 font-mono-num">
            <span className="flex-1 border-t border-slate-800" /> or <span className="flex-1 border-t border-slate-800" />
          </div>
          <button
            type="button"
            data-testid="google-signin-btn"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + "/auth/callback";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="mt-4 w-full py-2.5 rounded-md bg-white hover:bg-slate-100 text-slate-900 font-medium transition-colors inline-flex items-center justify-center gap-2.5"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z"/></svg>
            Sign in with Google
          </button>
          <div className="mt-6 text-sm text-slate-400 text-center">
            No account? <Link to="/signup" className="text-blue-400 hover:text-blue-300" data-testid="link-signup">Create one</Link>
          </div>
        </form>
        <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
          <DialogContent className="bg-slate-900 border-slate-800 text-slate-100" data-testid="forgot-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">Reset your password</DialogTitle>
            </DialogHeader>
            <p className="text-xs text-slate-500">Enter your account email and we&apos;ll send a reset link (valid for 1 hour) to your sign-in and recovery email.</p>
            <form onSubmit={sendReset}>
              <label className="block text-xs uppercase tracking-widest text-slate-400 font-mono-num mb-1.5">Email</label>
              <input
                data-testid="forgot-email-input"
                type="email" required value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-md bg-slate-950 border border-slate-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/40 outline-none text-sm"
                placeholder="you@company.com"
              />
              <button
                type="submit" disabled={forgotBusy}
                data-testid="forgot-submit-btn"
                className="mt-4 w-full py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium transition-colors disabled:opacity-60"
              >
                {forgotBusy ? "Sending…" : "Email me a reset link"}
              </button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
